/**
 * Users Hooks
 * Manages user data for admin panel
 */

import { useApi, useMutation } from './useApi';
import { adminAPI } from '@/services/api';
import type {
  UsersListResponse,
  UserDetailResponse,
  UserTransactionsResponse,
  RecreateUserResponse,
} from '@/types';

// =============================================================================
// Users List Hook
// =============================================================================

export interface UseUsersOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: UsersListResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching and searching users
 *
 * NOTE: Backend returns FLAT ARRAY, not { users, total, page }
 *
 * @example
 * const { users, isLoading, searchUsers, refetch } = useUsers({ fetchOnMount: true });
 * searchUsers('john');
 */
export function useUsers(options: UseUsersOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  const api = useApi<UsersListResponse, [{ q?: string }?]>(
    (params) => adminAPI.getUsers(params),
    { fetchOnMount, onSuccess, onError }
  );

  // Backend returns flat array
  const users = api.data ?? [];

  return {
    ...api,
    users,
    total: users.length,
    searchUsers: (query: string) => api.execute({ q: query }),
    refetch: () => api.execute(),
  };
}

// =============================================================================
// User Detail Hook
// =============================================================================

export interface UseUserDetailOptions {
  onSuccess?: (data: UserDetailResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching single user details
 *
 * NOTE: Backend returns { user, stats }, NOT { user, transactions }
 * Use useUserTransactions for transaction history
 *
 * @example
 * const { user, stats, isLoading, fetchUser } = useUserDetail();
 * fetchUser('123456789');
 */
export function useUserDetail(options: UseUserDetailOptions = {}) {
  const api = useApi<UserDetailResponse, [string]>(
    (telegramId) => adminAPI.getUserDetails(telegramId),
    options
  );

  return {
    ...api,
    user: api.data?.user ?? null,
    stats: api.data?.stats ?? null,
    fetchUser: api.execute,
  };
}

// =============================================================================
// User Transactions Hook
// =============================================================================

export interface UseUserTransactionsOptions {
  telegramId?: string;
  year?: number;
  month?: number;
  fetchOnMount?: boolean;
  onSuccess?: (data: UserTransactionsResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching user's transaction history
 *
 * @example
 * const { transactions, fetchTransactions } = useUserTransactions({ telegramId: '123456789' });
 * fetchTransactions({ year: 2025, month: 12 });
 */
export function useUserTransactions(options: UseUserTransactionsOptions = {}) {
  const { onSuccess, onError } = options;

  const api = useApi<UserTransactionsResponse, [string, { year?: number; month?: number }?]>(
    (telegramId, params) => adminAPI.getUserTransactions(telegramId, params),
    { onSuccess, onError }
  );

  return {
    ...api,
    transactions: api.data?.transactions ?? [],
    year: api.data?.year,
    month: api.data?.month,
    fetchTransactions: (telegramId: string, params?: { year?: number; month?: number }) =>
      api.execute(telegramId, params),
  };
}

// =============================================================================
// User Actions Hook
// =============================================================================

export interface UseUserActionsOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for user management actions (ban, unban, recreate)
 *
 * @example
 * const { banUser, unbanUser, recreateUser, isLoading } = useUserActions();
 * await banUser('123456789');
 */
export function useUserActions(options: UseUserActionsOptions = {}) {
  const banMutation = useMutation<{ message: string }, [string, string]>(
    (telegramId, reason) => adminAPI.banUser(telegramId, reason),
    { onSuccess: () => options.onSuccess?.(), onError: options.onError }
  );

  const unbanMutation = useMutation<{ message: string }, [string, string?]>(
    (telegramId, reason) => adminAPI.unbanUser(telegramId, reason),
    { onSuccess: () => options.onSuccess?.(), onError: options.onError }
  );

  const recreateMutation = useMutation<RecreateUserResponse, [string, string]>(
    (telegramId, reason) => adminAPI.recreateUser(telegramId, reason),
    { onSuccess: () => options.onSuccess?.(), onError: options.onError }
  );

  return {
    banUser: banMutation.mutate,
    isBanning: banMutation.isLoading,
    banError: banMutation.error,

    unbanUser: unbanMutation.mutate,
    isUnbanning: unbanMutation.isLoading,
    unbanError: unbanMutation.error,

    recreateUser: recreateMutation.mutate,
    isRecreating: recreateMutation.isLoading,
    recreateError: recreateMutation.error,
    recreateResult: recreateMutation.data,

    isLoading: banMutation.isLoading || unbanMutation.isLoading || recreateMutation.isLoading,
  };
}
